/**
 * Map widget helpers — build and parse a Google Maps *keyless* embed URL.
 *
 * The Map widget stores an ordinary website-family config: a sanitized `url`
 * plus `display_mode: "iframe_map"`. That URL is a Google Maps embed of the
 * form `https://www.google.com/maps?q=<place>&z=<zoom>&output=embed`, which
 * renders in the shared iframe pipeline (editor canvas, preview, web + device
 * players) with NO API key and NO credentials.
 *
 * We deliberately store only the `url` (no extra map-specific keys) so the
 * backend WebEmbedSanitizer round-trips the config unchanged. The inspector
 * form is reconstructed from the URL on open via parseMapEmbedUrl().
 */

export interface MapForm {
  /** Free-text address / place, e.g. "Oshawa, Ontario". Empty when using coords. */
  query: string;
  /** Optional manual latitude (string for controlled input). */
  lat: string;
  /** Optional manual longitude (string for controlled input). */
  lng: string;
  zoom: number;
  /** Drop a pin at the location. Keyless embeds always pin a `q=` query, so a
   *  pin is only suppressed when explicit coordinates are supplied. */
  marker: boolean;
}

export const DEFAULT_MAP_ZOOM = 14;
export const MIN_MAP_ZOOM = 1;
export const MAX_MAP_ZOOM = 21;

export const emptyMapForm = (): MapForm => ({
  query: "", lat: "", lng: "", zoom: DEFAULT_MAP_ZOOM, marker: true,
});

const clampZoom = (z: number): number =>
  Math.max(MIN_MAP_ZOOM, Math.min(MAX_MAP_ZOOM, Math.round(Number.isFinite(z) ? z : DEFAULT_MAP_ZOOM)));

const hasCoords = (f: MapForm): boolean => {
  const lat = f.lat.trim(), lng = f.lng.trim();
  return lat !== "" && lng !== "" && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
};

/**
 * Build the keyless Google Maps embed URL for a form, or "" when there is no
 * usable location yet (the widget then shows a "No URL set" message rather
 * than a broken frame).
 */
export const buildMapEmbedUrl = (f: MapForm): string => {
  const coords = hasCoords(f);
  const loc = coords ? `${Number(f.lat)},${Number(f.lng)}` : f.query.trim();
  if (!loc) return "";
  const zoom = clampZoom(f.zoom);
  const params = new URLSearchParams();
  // With a pin we use `q` (works for both an address and "lat,lng"). Without a
  // pin we can only reliably center on explicit coordinates via `ll`; an
  // address with no pin still falls back to `q` so the map never renders blank.
  if (!f.marker && coords) {
    params.set("ll", loc);
  } else {
    params.set("q", loc);
  }
  params.set("z", String(zoom));
  params.set("output", "embed");
  return `https://www.google.com/maps?${params.toString()}`;
};

/** Reconstruct the inspector form from a stored map embed URL. */
export const parseMapEmbedUrl = (url: string | null | undefined): MapForm => {
  const form = emptyMapForm();
  const s = (url ?? "").trim();
  if (!s) return form;
  try {
    const u = new URL(s);
    const z = Number(u.searchParams.get("z"));
    if (Number.isFinite(z) && z > 0) form.zoom = clampZoom(z);
    const q = u.searchParams.get("q");
    const ll = u.searchParams.get("ll");
    const loc = (q || ll || "").trim();
    if (ll && !q) form.marker = false;
    const m = loc.match(/^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/);
    if (m) { form.lat = m[1]; form.lng = m[2]; }
    else if (loc) { form.query = loc; }
  } catch { /* not a parseable URL — leave the empty form */ }
  return form;
};

/** True when the stored url looks like a usable Google Maps embed URL. */
export const isGoogleMapsEmbedUrl = (url: string | null | undefined): boolean => {
  const s = (url ?? "").trim();
  if (!s) return false;
  try {
    const u = new URL(s);
    const host = u.hostname.replace(/^www\./, "").toLowerCase();
    return (host === "google.com" || host === "maps.google.com") && u.pathname.includes("/maps");
  } catch { return false; }
};
